// Spotlights cache utility for web and browser extension
// Handles fetching, storing, and dismissing spotlights per tenant

import type { Spotlight } from '../api/spotlights';
import dayjs from 'dayjs';

// Types for dismissed spotlights
export interface DismissedSpotlight {
  spotlightType: string;
  dismissedOn: string; // ISO date string
}

// Legacy format: array of strings
type LegacyDismissedSpotlights = string[];

// New format: array of objects with timestamp
type NewDismissedSpotlights = DismissedSpotlight[];

// Union type for both formats
type DismissedSpotlights = LegacyDismissedSpotlights | NewDismissedSpotlights;

// Detect if running in Chrome extension
export function isChromeExtension(): boolean {
  return typeof chrome !== 'undefined' && !!chrome.storage?.sync;
}

function getSpotlightsKey(tenantId: string) {
  return `spotlights_${tenantId}`;
}

function getDismissedKey(tenantId: string) {
  return `spotlights_dismissed_${tenantId}`;
}

// Helper function to check if a spotlight should be dismissed
// A spotlight is dismissed if it was created before the dismissal date
function shouldSpotlightBeDismissed(
  spotlight: Spotlight,
  dismissedOn: string,
): boolean {
  const spotlightCreatedDate = dayjs(spotlight.createdAt);
  const dismissalDate = dayjs(dismissedOn);
  return spotlightCreatedDate <= dismissalDate;
}

// Helper function to get raw dismissed data in new format
async function getRawDismissedData(
  tenantId: string,
): Promise<NewDismissedSpotlights> {
  const key = getDismissedKey(tenantId);
  let rawData: DismissedSpotlights = [];

  if (isChromeExtension()) {
    rawData = await new Promise((resolve) => {
      chrome.storage.sync.get([key], (result) => {
        if (result[key]) {
          try {
            resolve(JSON.parse(String(result[key])));
          } catch {
            resolve([]);
          }
        } else {
          resolve([]);
        }
      });
    });
  } else {
    const cached = localStorage.getItem(key);
    if (cached) {
      try {
        rawData = JSON.parse(cached);
      } catch {
        rawData = [];
      }
    }
  }

  if (!Array.isArray(rawData)) {
    return [];
  }

  // If it's already in new format, return as is
  if (
    rawData.length > 0 &&
    typeof rawData[0] === 'object' &&
    'spotlightType' in rawData[0]
  ) {
    const newFormatData = rawData as NewDismissedSpotlights;
    return newFormatData;
  }

  // Convert legacy format to new format (treat as dismissed on September 17, 2025)
  const legacyData = rawData as LegacyDismissedSpotlights;
  const legacyDismissalDate = new Date(
    '2025-09-17T00:00:00.000Z',
  ).toISOString();
  return legacyData
    .filter((id) => typeof id === 'string')
    .map((id) => ({
      spotlightType: id,
      dismissedOn: legacyDismissalDate,
    }));
}

// Get dismissed spotlights filtered by creation date
export async function getDismissedSpotlights(
  tenantId: string,
  spotlights: Spotlight[],
): Promise<string[]> {
  const rawDismissed = await getRawDismissedData(tenantId);

  // Filter dismissed spotlights based on creation date
  const validDismissals = rawDismissed.filter((dismissedItem) => {
    const spotlight = spotlights.find(
      (s) => s.spotlightType === dismissedItem.spotlightType,
    );
    // If spotlight doesn't exist, keep the dismissal (it might be for future spotlights)
    if (!spotlight) {
      return true;
    }
    // Only keep dismissal if spotlight was created before dismissal date
    return shouldSpotlightBeDismissed(spotlight, dismissedItem.dismissedOn);
  });

  return validDismissals.map((item) => item.spotlightType);
}

// Add a spotlight ID to dismissed list for a tenant
export async function dismissSpotlight(
  tenantId: string,
  spotlightType: string,
): Promise<void> {
  const key = getDismissedKey(tenantId);
  const currentDismissed = await getRawDismissedData(tenantId);
  const currentDate = new Date().toISOString();

  // Check if already dismissed
  const existingDismissalIndex = currentDismissed.findIndex(
    (item) => item.spotlightType === spotlightType,
  );

  if (existingDismissalIndex !== -1) {
    // Update existing dismissal date to current date
    currentDismissed[existingDismissalIndex].dismissedOn = currentDate;
  } else {
    // Add new dismissal
    const newDismissal: DismissedSpotlight = {
      spotlightType,
      dismissedOn: currentDate,
    };
    currentDismissed.push(newDismissal);
  }

  const str = JSON.stringify(currentDismissed);

  if (isChromeExtension()) {
    return new Promise((resolve) => {
      chrome.storage.sync.set({ [key]: str }, () => resolve());
    });
  } else {
    localStorage.setItem(key, str);
  }
}

// Clear spotlights for a tenant (for cleanup or refresh)
export async function clearSpotlights(tenantId: string): Promise<void> {
  const key = getSpotlightsKey(tenantId);
  if (isChromeExtension()) {
    return new Promise((resolve) => {
      chrome.storage.sync.remove([key], () => resolve());
    });
  } else {
    localStorage.removeItem(key);
  }
}
